import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Divider, Image, Row, Space, Spin, Typography, Upload } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Company } from 'src/features/companies/companies.types';
import { DeleteOutlined, PictureOutlined, ShopTwoTone } from '@ant-design/icons';
import { fetchCompany } from 'src/features/companies/companies.state';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import { AppDispatch } from 'src/index';
import MainLayout from 'src/components/layouts/MainLayout';
import {
  useBannerImage,
  useCompanyBannerById,
  useCompanyBannersState,
} from 'src/features/companyBanners/companyBanners.hooks';
import { CompanyBanner, CompanyBannerType } from 'src/features/companyBanners/companyBanners.types';
import {
  deleteCompanyBanner,
  fetchCompanyBanner,
  updateCompanyBanner,
} from 'src/features/companyBanners/companyBanners.state';
import { UploadFile } from 'antd/es/upload/interface';
import InputEditableField from 'src/components/fields/InputEditableField';
import { formatDatetime } from 'src/helpers/formatters';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import tileStyles from 'src/styles/tile.module.css';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import SelectEditableField from 'src/components/fields/SelectEditableField';
import { formatBannerType } from 'src/utils/utils';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

const SortableItem: React.FC<PropsWithChildren<{ id: string; key: string; style?: React.CSSProperties }>> = props => {
  const { children, style } = props;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const innerStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...style,
  } as React.CSSProperties;
  return (
    <div style={innerStyle} ref={setNodeRef} {...attributes} {...listeners} className={tileStyles.listTile}>
      {children}
    </div>
  );
};

const CompanyBannerPage: React.FC = () => {
  const { bannerId, companyId } = useParams() as {
    bannerId: string;
    companyId: string;
  };
  const dispatch: AppDispatch = useDispatch();
  const banner = useCompanyBannerById(bannerId) as CompanyBanner;
  const state = useCompanyBannersState();
  const company = useCompanyById(companyId) as Company;
  const { uploadBannerImage } = useBannerImage({
    bannerId,
    companyId,
  });
  const history = useHistory();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChange = useCallback(
    (file: UploadFile) => {
      uploadBannerImage(file as unknown as File);
    },
    [uploadBannerImage]
  );

  const handleBannerUpdate = useCallback(
    (data: Partial<Omit<CompanyBanner, 'id'>>) => {
      dispatch(updateCompanyBanner({ companyId, bannerId, data }));
    },
    [companyId, bannerId, dispatch]
  );

  const handleDeleteImage = useCallback(
    (url: string) => {
      const { images } = banner;
      if (images) {
        handleBannerUpdate({ images: images.filter(i => i !== url) });
      }
    },
    [banner, handleBannerUpdate]
  );

  const handleDeleteButtonClick = useCallback(async () => {
    const response = await dispatch(deleteCompanyBanner({ bannerId, companyId }));
    if (deleteCompanyBanner.fulfilled.match(response)) {
      history.push(`/commercial/companies/${companyId}/banners`);
    }
  }, [companyId, dispatch, bannerId, history]);
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      const { images } = banner;

      if (active.id !== over?.id && images) {
        const oldIndex = images.findIndex(i => i === active.id);
        const newIndex = images.findIndex(i => i === over?.id);
        const result = arrayMove(images, oldIndex, newIndex);
        await handleBannerUpdate({ images: result });
      }
    },
    [handleBannerUpdate, banner]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/companies'>
                <Space>
                  <ShopTwoTone />
                  Компании
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/commercial/companies/${companyId}`}>{company.name}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Space>
                <PictureOutlined />
                <Link to={`/commercial/companies/${companyId}/banners`}>Баннеры</Link>
              </Space>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{banner.description}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {banner.description}
          </Typography.Title>
          <LoaderWrapper loading={state.isPending}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={banner.images || []} strategy={horizontalListSortingStrategy}>
                <Space direction={'horizontal'} wrap>
                  {banner.images?.map(url => (
                    <SortableItem
                      key={url}
                      id={url}
                      style={{
                        width: 220,
                        height: 220,
                        display: 'flex',
                        alignItems: 'center',
                        padding: 8,
                        position: 'relative',
                        backgroundColor: '#fff',
                      }}
                    >
                      <ConfirmButton
                        danger
                        size={'small'}
                        type={'link'}
                        onClick={() => handleDeleteImage(url)}
                        style={{ position: 'absolute', top: 4, right: 4 }}
                        icon={<DeleteOutlined />}
                      />
                      <Image src={url} width={'100%'} />
                    </SortableItem>
                  ))}
                </Space>
              </SortableContext>
            </DndContext>
          </LoaderWrapper>
        </div>
        <div className={styles.sidebar}>
          <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
            <Upload
              beforeUpload={() => false}
              accept={'image/*'}
              showUploadList={false}
              onChange={e => handleChange(e.file)}
            >
              <Button type={'primary'}>Загрузить баннеры</Button>
            </Upload>
          </ComponentRoleProtected>
          <div className={styles.propertiesGrid}>
            <Typography.Text className={styles.title}>Описание</Typography.Text>
            <InputEditableField value={banner.description} onBlur={e => handleBannerUpdate({ description: e })} />
            <Typography.Text className={styles.title}>Тип</Typography.Text>
            <SelectEditableField
              value={banner.bannerType}
              options={[
                { label: formatBannerType(CompanyBannerType.AdBanner), value: CompanyBannerType.AdBanner },
                { label: formatBannerType(CompanyBannerType.SystemBanner), value: CompanyBannerType.SystemBanner },
                { label: formatBannerType(CompanyBannerType.CompanyBanner), value: CompanyBannerType.CompanyBanner },
              ]}
              onSelect={e => handleBannerUpdate({ bannerType: e as CompanyBannerType })}
            />
          </div>
          <Divider />
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>
              Создано: {formatDatetime(banner.createdAt)}
            </Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              Обновлено: {formatDatetime(banner.updatedAt)}
            </Typography.Text>
            <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
              <Button type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
                <DeleteOutlined />
                Удалить баннер
              </Button>
            </ComponentRoleProtected>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CompanyBannerPageBlock: React.FC = () => {
  const { bannerId, companyId } = useParams() as {
    bannerId: string;
    companyId: string;
  };
  const dispatch = useDispatch();
  const banner = useCompanyBannerById(bannerId);
  const company = useCompanyById(companyId);

  useEffect(() => {
    dispatch(fetchCompany({ companyId }));
    dispatch(fetchCompanyBanner({ bannerId, companyId }));
  }, [companyId, dispatch, bannerId]);

  if (!banner || !company) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <CompanyBannerPage />;
};
export default CompanyBannerPageBlock;
